"use client";

import NextLink from "next/link";
import { notFound } from "next/navigation";
import * as React from "react";

import { AccessCheck } from "@/components/access-check";
import parse from "@/components/html-parser";
import { References } from "@/components/references";
import ResearchSubscription from "@/components/research-subscription";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Link from "@/components/ui/link";
import { Page } from "@/types";

interface OutlineProps {
	researchBreakdown: any;
	link?: string;
}

const Outline = ({ researchBreakdown, link = "" }: OutlineProps) => {
	return (
		<div className="relative z-10 w-full pb-20 pl-4 md:pl-6">
			{researchBreakdown?.map((item: any, index: any) => (
				<React.Fragment key={item.id}>
					<div>
						<NextLink
							href={`${link}#${item.id}`}
							className="flex items-center space-x-4 font-lora text-xl font-medium xl:text-[26px]"
						>
							<p className="text-md w-14 rounded bg-primary py-2 text-center text-white md:px-4 lg:w-16">
								{index + 1}
							</p>
							<p className="py-8 text-primary hover:underline lg:text-xl xl:text-[26px]">
								{item.text}
							</p>
						</NextLink>
					</div>

					<div className="ml-4 flex w-full flex-col space-y-6 border-l-2 border-primary ">
						{item?.children?.map(
							(
								children: {
									id: React.Key | null | undefined;
									text:
										| string
										| number
										| boolean
										| React.ReactElement<any, string | React.JSXElementConstructor<any>>
										| Iterable<React.ReactNode>
										| React.ReactPortal
										| null
										| undefined;
								},
								idx: number
							) => (
								<div key={children.id} id={`ref-${children.id}`}>
									<div
										className={`relative flex w-full items-center justify-between text-left  lg:hover:text-primary `}
									>
										<NextLink
											href={`${link}#${item.id}_${children.id}`}
											className="flex items-center font-lora font-medium"
										>
											<span className="h-0.5 w-7 bg-primary md:w-10"></span>
											<div className="flex items-center space-x-4">
												<p className="w-16 rounded bg-primary px-4 py-2 text-center text-white xl:w-20">
													{index + 1}.{idx + 1}
												</p>
												<p className="text-base text-primary hover:underline xl:text-22">
													{children.text}
												</p>
											</div>
										</NextLink>
									</div>
								</div>
							)
						)}
					</div>
				</React.Fragment>
			))}
		</div>
	);
};

interface ResearchBreakdownOutlineProps {
	researchBreakdown: any;
	page?: Page;
}

const ResearchBreakdownOutline = ({ researchBreakdown, page }: ResearchBreakdownOutlineProps) => {
	if (!researchBreakdown?.length) return null;

	const { type, slug } = page;
	const researchBreakdownHref = `/${type}/${slug}/research/`;

	return (
		<Accordion type="single" id="research-breakdown" defaultValue="research-breakdown" collapsible>
			<AccordionItem value="research-breakdown">
				<AccordionTrigger
					copyLinkUrl={`${page?.url}/#research-breakdown`}
					as="h2"
					className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
				>
					<span>Research Breakdown</span>
				</AccordionTrigger>

				<AccordionContent>
					<Alert
						variant="warning"
						className="my-6 rounded-lg border-l-8 border-[#f2ce72] bg-[#f1cd7833] px-8 py-4"
					>
						<AlertDescription>
							The{" "}
							<Link href={researchBreakdownHref} rel="nofollow">
								Examine Research Breakdown
							</Link>{" "}
							has been archived. The Examine team will be incorporating the information in this
							section throughout the site after a thorough review to ensure it is up to date.
						</AlertDescription>
					</Alert>
					{/* <Outline researchBreakdown={researchBreakdown} link="research/" /> */}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};

const ResearchBreakdownPage = ({ researchBreakdown, page }: ResearchBreakdownOutlineProps) => {
	const { research_breakdown, references } = researchBreakdown;

	if (!research_breakdown?.length) {
		notFound();
	}

	return (
		<>
			<Outline researchBreakdown={research_breakdown} />
			<hr />
			<br />
			<br />
			<AccessCheck feature={["examine_database", "study_summaries"]}>
				<ResearchSubscription className={"!mx-auto"} placement="research-breakdown-section" />
			</AccessCheck>
			<div className="prose relative z-10 my-8 w-full pl-6 lg:prose-lg xl:prose-xl">
				{research_breakdown?.map(
					(
						item: {
							id: any;
							text:
								| string
								| number
								| boolean
								| React.ReactElement<any, string | React.JSXElementConstructor<any>>
								| Iterable<React.ReactNode>
								| React.ReactPortal;
							content: string;
							children: any[];
						},
						index: number
					) => (
						<div key={index}>
							<div
								id={`${item.id}`}
								className=" mt-8 inline-flex gap-4 font-lora text-30 font-medium text-primary xl:text-40"
							>
								<p>{index + 1}.</p>
								<p>{item.text}</p>
							</div>
							<div className="research-container text-content">
								{item.content && parse(item.content)}
							</div>
							<div className="w-full pt-2 ">
								{item?.children?.map((children, idx) => (
									<div key={children.id} id={`${item.id}_${children.id}`}>
										<div className={`relative flex w-full items-center justify-between text-left`}>
											<div className="space-y-6 xl:text-30">
												<div className="flex gap-4 pt-8 font-lora text-22 font-medium">
													<p className="text-primary">
														{index + 1}.{idx + 1}
													</p>
													<p className="text-primary">{children.text}</p>
												</div>
												<div className="research-container text-content mt-16">
													{children.content && parse(children.content)}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)
				)}
			</div>

			<References references={references} accordionsState={undefined} page={page} />
		</>
	);
};

export { ResearchBreakdownOutline, Outline, ResearchBreakdownPage };
